import { forwardRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { SwapKitNumber } from "@swapkit/core";
import { cva, VariantProps } from "class-variance-authority";
import { ChevronDown } from "lucide-react";

import Check from "~/assets/check.svg";
import runeIcon from "~/assets/rune-icon-color.png";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui";
import { topData } from "../../app/mockData";
import { ComingSoonOverlay } from "./ComingSoonOverlay";

type TopListProps = {
  type: "categories" | "sales" | "offers" | "profiles";
};

export default function TopList({ type }: TopListProps) {
  const titleByType = {
    categories: "Top Categories",
    sales: "Top Sales",
    offers: "Top Offers",
    profiles: "Top Profiles",
  };

  return (
    <div className="relative">
      <ComingSoonOverlay />

      <TopListHeader title={titleByType[type]}>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger className="ml-auto" asChild disabled>
            <Button variant="ghost" size="sm">
              24h
              <ChevronDown className="ml-0.5 w-5 md:-mr-1 md:ml-0 md:w-6" />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent>
            <DropdownMenuItem>Price low to high</DropdownMenuItem>

            <DropdownMenuItem>Price high to low</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TopListHeader>

      <div className="bg-black/50 py-2">
        {topData?.categories?.map((category, index) => {
          const parsedPrice = new SwapKitNumber({ value: category?.price?.rune / 100, decimal: 2 });

          return (
            <Link
              href={`/tns/${category?.name}`}
              key={`${category?.name}-${index}`}
              className="flex items-center px-6 py-2"
            >
              <Image
                src={category?.icon}
                priority
                alt={category?.name}
                className="h-4 w-4 md:h-5 md:w-5"
              />

              <div className="ml-1.5 text-sm md:ml-2 md:text-base">{category?.name}</div>

              {category?.isVerified && (
                <Check className="ml-1 h-[11px] w-[11px] fill-[#8C8C8C] md:h-3 md:w-3" />
              )}

              <div className="ml-auto flex items-center space-x-1">
                <Image alt="rune" src={runeIcon} priority className="w-3 md:w-3.5" />

                <div className="text-xs font-semibold leading-none md:text-sm md:font-medium">
                  {parsedPrice.toFixed(2)}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

const topListVariants = cva("flex items-center", {
  variants: {
    variant: {
      heading: "border-b border-[#2D2226] pb-5 text-[28px] leading-none",
      nav: "bg-black/30 px-4 py-2 text-[15px] font-medium uppercase md:px-6 md:py-3 md:text-lg leading-none",
    },
  },
  defaultVariants: {
    variant: "nav",
  },
});

interface PriceWithCurrencyProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof topListVariants> {
  asChild?: boolean;
  children?: React.ReactNode;
}

export const TopListHeader = forwardRef<HTMLDivElement, PriceWithCurrencyProps>(
  ({ title, className, variant, children }, ref) => (
    <div className={topListVariants({ className, variant })} ref={ref}>
      <h3 className="mr-2 overflow-hidden overflow-ellipsis whitespace-nowrap leading-normal md:leading-snug">
        {title}
      </h3>

      {children}
    </div>
  ),
);

TopListHeader.displayName = "TopListHeader";
